import {
  Banner,
  Button,
  Caption,
  Checkbox,
  Fieldset,
  Icon,
  Radio,
  Stack,
  TextField,
} from '@mediahuis/chameleon-react';
import { Error } from '@mediahuis/chameleon-theme-wl/icons';
import { Form, Formik } from 'formik';
import React, { ChangeEvent, FC, useState, useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useBrandGroupConfig } from '../hooks/useBrandGroupConfig';
import { useQueryParams } from '../hooks/useQueryParams';
import { useTracking } from '../hooks/useTracking';
import { useTranslatedErrorCodes } from '../hooks/useTranslatedErrorCodes';
import { useAuthenticationContext } from '../providers/AuthenticationProvider';
import { RootState } from '../redux/store';
import { registrationCompletionService } from '../services/registrationCompletionService';
import { NotFoundError } from '../types/general';
import {
  formSchema,
  GENDER_TYPES,
  GenderType,
  RegistrationCompletionFormValues,
} from '../types/schemas/registerCompletionSchema';
import { EventActionValues, EventLabelValues } from '../types/tracking';
import DatePicker from './WrapperComponents/DatePicker';

import { OptInState } from '../types/registrationFlows';
import './registrationCompletionForm.css';

interface RegistrationCompletionFormProps {
  whenSubmitted?: () => void;
  email: string | undefined;
}

const RegistrationCompletionForm: FC<RegistrationCompletionFormProps> = (props) => {
  // Uses
  const { translateError } = useTranslatedErrorCodes();
  const brandGroupCfg = useBrandGroupConfig();
  const { brandConfig } = useBrandGroupConfig();
  const { t } = useTranslation();
  const validationProperties = useSelector((state: RootState) => state.validationProperties);
  const { user, getAccessToken } = useAuthenticationContext();
  const { brand } = useQueryParams();
  const { trackEvent } = useTracking();

  // Form
  const [registrationCompletionErrors, setRegistrationCompletionErrors] = useState<string[]>([]);
  const [currentGender, setCurrentGender] = useState<GenderType>();
  const [showMinimumAgeWarning, setShowMinimumAgeWarning] = useState<boolean>(false);
  const genderIsChecked = (value: string) => value === currentGender;

  const minimumRegistrationAge = brandConfig?.minimumRegistrationAge ?? null;
  const formValuesRef = useRef<{
    birthDateDay?: string;
    birthDateMonth?: string;
    birthDateYear?: string;
  }>({});

  const checkAge = useCallback(
    (day?: string, month?: string, year?: string) => {
      if (!day || !month || !year || year.length < 4 || minimumRegistrationAge === null) {
        setShowMinimumAgeWarning(false);
        return;
      }

      const birthDate = dayjs(`${year}-${month}-${day}`);
      const today = dayjs();
      const age = today.diff(birthDate, 'year');

      setShowMinimumAgeWarning(
        birthDate.isValid() && birthDate.isBefore(today) && age < minimumRegistrationAge
      );
    },
    [minimumRegistrationAge]
  );

  const submitRegistrationCompletion = async (values: RegistrationCompletionFormValues) => {
    const accessToken = await getAccessToken();

    if (brand && user && user.sub && accessToken) {
      setRegistrationCompletionErrors([]);
      await registrationCompletionService
        .completeDefaultRegistration(user.sub, accessToken, values)
        .then(() => {
          props.whenSubmitted && props.whenSubmitted();
          trackEvent(EventActionValues.Click, EventLabelValues.PersonalInfoContinue);
        })
        .catch((e: Error) => setRegistrationCompletionErrors(translateError(e, 'register.error')));
      const optinsToSave: Array<OptInState> = [];
      // lunchupdate and offers are hardcoded in the MHNL flow for now.
      optinsToSave.push({ code: 'lunchupdate', active: !!values.optinNewsletter });
      optinsToSave.push({ code: 'offers', active: !!values.optinPromotions });
      await registrationCompletionService
        .sendUserPreferences(user.sub, props.email ?? '', brand, accessToken, optinsToSave)
        .catch((_) => {
          // ignore error because user preferences dependency may not block registration flow
        });
    } else {
      // TODO: Improve error
      throw new NotFoundError('Missing required parameters');
    }
  };

  const showCustomGender = () => {
    return currentGender === GenderType.OTHER.toUpperCase();
  };

  return (
    <Stack>
      <Banner
        show={
          registrationCompletionErrors?.length !== undefined &&
          registrationCompletionErrors.length > 0
        }
        onClose={() => setRegistrationCompletionErrors([])}
        appearance="error"
        className="mx-auto">
        {registrationCompletionErrors?.map((error, i) => {
          return (
            <Caption size="lg" key={i}>
              {error}
            </Caption>
          );
        })}
      </Banner>
      <Formik
        initialValues={{} as RegistrationCompletionFormValues}
        validationSchema={formSchema(validationProperties)}
        onSubmit={(values) => submitRegistrationCompletion(values)}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}>
        {({ errors, setFieldValue, isSubmitting, values }) => {
          const hasError =
            !!errors.birthDateYear || !!errors.birthDateMonth || !!errors.birthDateDay;

          return (
            <Form noValidate={true}>
              <Stack flexDirection="column">
                <Fieldset label={t('register.birthDate.date')} className="label bold_label">
                  <Stack flexDirection="column" className="mb-5">
                    <DatePicker
                      day={{
                        fieldName: 'birthDateDay',
                        error: !!errors.birthDateDay,
                        value: values.birthDateDay?.toString(),
                        label: t('register.birthDate.day'),
                        onChange: (value: string) => {
                          setFieldValue('birthDateDay', value, !!errors.birthDateDay);
                          formValuesRef.current.birthDateDay = value;
                          checkAge(
                            value,
                            formValuesRef.current.birthDateMonth,
                            formValuesRef.current.birthDateYear
                          );
                        },
                      }}
                      month={{
                        fieldName: 'birthDateMonth',
                        value: values.birthDateMonth?.toString(),
                        error: !!errors.birthDateMonth,
                        label: t('register.birthDate.month'),
                        onChange: (value: string) => {
                          setFieldValue('birthDateMonth', value, !!errors.birthDateMonth);
                          formValuesRef.current.birthDateMonth = value;
                          checkAge(
                            formValuesRef.current.birthDateDay,
                            value,
                            formValuesRef.current.birthDateYear
                          );
                        },
                      }}
                      year={{
                        value: values.birthDateYear?.toString(),
                        fieldName: 'birthDateYear',
                        error: !!errors.birthDateYear,
                        label: t('register.birthDate.year'),
                        onChange: (value: string) => {
                          setFieldValue('birthDateYear', value, !!errors.birthDateYear);
                          formValuesRef.current.birthDateYear = value;
                          checkAge(
                            formValuesRef.current.birthDateDay,
                            formValuesRef.current.birthDateMonth,
                            value
                          );
                        },
                      }}
                    />
                  </Stack>
                  {hasError && (
                    <Stack alignItems="center" className="mt-1" flexDirection="row">
                      <Icon as={Error} color="redBase" className="mr-r" />
                      <Caption
                        as="div"
                        size="lg"
                        className="red-base m-0"
                        style={{ textAlign: 'left' }}>
                        {t('register.error.invalidBirthDate')}
                      </Caption>
                    </Stack>
                  )}
                  {showMinimumAgeWarning && !hasError && (
                    <Stack
                      className="error-tooltip mt-2"
                      flexDirection={'row'}
                      data-cy="minimum-age-warning">
                      <Caption
                        size="lg"
                        className="m-0 red-base"
                        style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Icon as={Error} size="sm" className="mr-2" />
                        {t('register.error.minimumAge', { minimumAge: minimumRegistrationAge })}
                      </Caption>
                    </Stack>
                  )}
                </Fieldset>
                <Fieldset label={t('register.gender.label')} className="mt-6 label bold_label">
                  <Stack
                    className="fieldset__gender"
                    flexDirection={{
                      sm: 'row',
                      md: 'row',
                      lg: 'row',
                      xl: 'row',
                    }}>
                    {GENDER_TYPES.map((gender) => (
                      <div key={`gender-${gender}`} className="pr-4">
                        <Radio
                          className="fieldset__gender__radio"
                          id={`genderType-${gender}`}
                          name="genderType"
                          value={gender}
                          label={t(`register.gender.values.${gender.toLowerCase()}`)}
                          onChange={(e: { target: { value: string } }) => {
                            setCurrentGender(e.target.value as GenderType);
                            setFieldValue('genderType', e.target.value, !!errors.genderType);
                          }}
                          checked={genderIsChecked(gender)}
                          required={true}
                          error={!!errors.genderType}
                        />
                      </div>
                    ))}
                  </Stack>
                  {showCustomGender() && (
                    <div className="my-3">
                      <TextField
                        id="genderCustom"
                        name="genderCustom"
                        label="custom gender"
                        labelHidden
                        type="text"
                        placeholder={t('register.gender.label')}
                        autoFocus
                        error={errors.genderCustom != null}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('genderCustom', e.target.value, !!errors.genderCustom)
                        }></TextField>
                    </div>
                  )}
                  {errors.genderType && (
                    <Stack alignItems="center" className="mt-1">
                      <Icon as={Error} color="redBase" className="mr-2 -ml-1" />
                      <Caption as="div" size="lg">
                        {t('register.error.invalidGender')}
                      </Caption>
                    </Stack>
                  )}
                  {!errors.genderType && showCustomGender() && errors.genderCustom && (
                    <Stack alignItems="center" className="mt-1">
                      <Icon as={Error} color="redBase" className="mr-2 -ml-1" />
                      <Caption as="div" size="lg">
                        {t('register.error.invalidGenderCustom')}
                      </Caption>
                    </Stack>
                  )}
                </Fieldset>
                <Stack className="mt-11">
                  <Checkbox
                    id="register__form-optin-newsletter"
                    label={
                      <Trans
                        i18nKey="optIns.newsletters" // optional -> fallbacks to defaults if not provided
                        values={{
                          brand: brandGroupCfg.getBrandConfigFromBrandId(brand)?.name,
                        }}
                        components={{ strong: <strong /> }}
                      />
                    }
                    labelProps={{ className: 'd-unset' }}
                    className="mb-1"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('optinNewsletter', e.target.checked)
                    }
                  />
                  <Checkbox
                    id="register__form-optin-offers"
                    className="mt-0"
                    label={
                      <Trans
                        i18nKey="optIns.promotions" // optional -> fallbacks to defaults if not provided
                        values={{
                          brand: brandGroupCfg.getBrandConfigFromBrandId(brand)?.name,
                        }}
                        components={{ strong: <strong /> }}
                      />
                    }
                    labelProps={{ className: 'd-unset' }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('optinPromotions', e.target.checked)
                    }
                  />
                </Stack>
                <Button
                  type="submit"
                  size="lg"
                  width="full"
                  className="mt-11"
                  appearance="primary"
                  loading={isSubmitting}>
                  <Stack alignItems="center">{t('register.confirmSignUp')}</Stack>
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
};
export default RegistrationCompletionForm;
